import React, { useState, useEffect } from 'react';
import { X, Share2, Trash2, UserPlus, FileText, AlertTriangle, Clock, CheckSquare, Square, Pencil, Eye } from 'lucide-react';
import apiService from '../services/apiService';
import { useLanguage } from '../i18n';
import { userErrorMessage } from '../utils/userError';
import Modal from './ui/Modal';

const ShareDialog = ({ isOpen, onClose, conversationId }) => {
    const { t } = useLanguage();
    const [email, setEmail] = useState('');
    const [shares, setShares] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    // Granular write permission for the NEXT share (Agenda TO Contacts Phase D /
    // T6). Threaded into shareConversation; existing-share rows show a chip.
    const [canWrite, setCanWrite] = useState(false);

    // Confirmation step state
    const [confirmStep, setConfirmStep] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [pendingEmail, setPendingEmail] = useState('');

    // Document selection state
    const [selectedDocIds, setSelectedDocIds] = useState(new Set());

    // Recent contacts
    const [contacts, setContacts] = useState([]);

    useEffect(() => {
        if (isOpen && conversationId) {
            loadShares();
            loadContacts();
            setEmail('');
            setError(null);
            setSuccess(null);
            setConfirmStep(false);
            setDocuments([]);
            setSelectedDocIds(new Set());
            setPendingEmail('');
            setCanWrite(false);
        }
        // Runs when the dialog opens / target changes. `loadShares` is a plain
        // (unmemoized) function recreated each render, so listing it would re-run
        // this reset effect on every render.
    }, [isOpen, conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadShares = async () => {
        try {
            const data = await apiService.getConversationShares(conversationId);
            setShares(data.shares || []);
        } catch (err) {
            console.error('Error loading shares:', err);
        }
    };

    const loadContacts = async () => {
        try {
            const data = await apiService.getShareContacts();
            setContacts(data.contacts || []);
        } catch (err) {
            console.error('Error loading share contacts:', err);
        }
    };

    const handleShareClick = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        await initiateShare(email.trim());
    };

    const initiateShare = async (targetEmail) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // Fetch documents used in this conversation
            setLoadingDocs(true);
            const data = await apiService.getConversationDocuments(conversationId);
            const docs = data.documents || [];
            setLoadingDocs(false);

            if (docs.length > 0) {
                // Documents exist — show confirmation step with all selected by default
                setDocuments(docs);
                setSelectedDocIds(new Set(docs.map(d => d.id)));
                setPendingEmail(targetEmail);
                setConfirmStep(true);
            } else {
                // No documents — share directly
                await executeShare(targetEmail, []);
            }
        } catch (err) {
            setError(userErrorMessage(err, t));
            setLoadingDocs(false);
        } finally {
            setLoading(false);
        }
    };

    const executeShare = async (targetEmail, sharedDocumentIds) => {
        setLoading(true);
        setError(null);

        try {
            await apiService.shareConversation(conversationId, targetEmail, sharedDocumentIds, canWrite);
            setSuccess(t('navSharedWithEmail', 'Shared with {email}', { email: targetEmail }));
            setEmail('');
            setConfirmStep(false);
            setDocuments([]);
            setPendingEmail('');
            setCanWrite(false);
            await loadShares();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmShare = () => {
        const docIds = Array.from(selectedDocIds);
        executeShare(pendingEmail, docIds);
    };

    const toggleDocSelection = (docId) => {
        setSelectedDocIds(prev => {
            const next = new Set(prev);
            if (next.has(docId)) {
                next.delete(docId);
            } else {
                next.add(docId);
            }
            return next;
        });
    };

    const selectAllDocs = () => {
        setSelectedDocIds(new Set(documents.map(d => d.id)));
    };

    const deselectAllDocs = () => {
        setSelectedDocIds(new Set());
    };

    const handleCancelConfirm = () => {
        setConfirmStep(false);
        setDocuments([]);
        setSelectedDocIds(new Set());
        setPendingEmail('');
    };

    const handleUnshare = async (userId) => {
        try {
            await apiService.unshareConversation(conversationId, userId);
            setShares(prev => prev.filter(s => s.shared_with_user_id !== userId));
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
    };

    const handleContactClick = (contactEmail) => {
        setEmail(contactEmail);
        initiateShare(contactEmail);
    };


    if (!isOpen) return null;

    // Filter contacts: exclude users already shared with on this conversation
    const sharedUserIds = new Set(shares.map(s => s.shared_with_user_id));
    const availableContacts = contacts.filter(c => !sharedUserIds.has(c.user_id));

    return (
        <Modal onClose={onClose}>
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-pai-subtle">
                    <div className="flex items-center gap-2">
                        <Share2 size={20} className="text-blue-500" />
                        <h3 className="text-lg font-semibold text-white">
                            {t('navShareConversationTitle', 'Share Conversation')}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-pai-faint hover:text-white  transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {confirmStep ? (
                    /* Document confirmation step */
                    <div className="p-4">
                        <div className="flex items-start gap-3 mb-3">
                            <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-white">
                                    {t('navShareReferencesDocs', 'This conversation references {n} document{s}', { n: documents.length, s: documents.length !== 1 ? 's' : '' })}
                                </p>
                                <p className="text-xs text-pai-meta mt-1">
                                    {t('navSelectDocsPrefix', 'Select which documents')} <strong>{pendingEmail}</strong> {t('navSelectDocsSuffix', 'will be able to preview and download:')}
                                </p>
                            </div>
                        </div>

                        {/* Select / Deselect all controls */}
                        <div className="flex items-center justify-between mb-2 ml-1">
                            <span className="text-xs text-pai-meta">
                                {t('navOfSelected', '{selected} of {total} selected', { selected: selectedDocIds.size, total: documents.length })}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={selectAllDocs}
                                    disabled={selectedDocIds.size === documents.length}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-default"
                                >
                                    {t('navSelectAll', 'Select all')}
                                </button>
                                <span className="text-xs text-white/25 text-pai-faint">|</span>
                                <button
                                    type="button"
                                    onClick={deselectAllDocs}
                                    disabled={selectedDocIds.size === 0}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-default"
                                >
                                    {t('navDeselectAll', 'Deselect all')}
                                </button>
                            </div>
                        </div>

                        <ul className="space-y-1.5 mb-4 ml-1 max-h-40 overflow-y-auto">
                            {documents.map(doc => {
                                const isSelected = selectedDocIds.has(doc.id);
                                return (
                                    <li
                                        key={doc.id}
                                        onClick={() => toggleDocSelection(doc.id)}
                                        className={`flex items-center gap-2 p-2 rounded text-sm cursor-pointer transition-colors ${
                                            isSelected
                                                ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                                                : 'bg-white/[0.05] bg-white/[0.06] hover:bg-white/[0.07] '
                                        }`}
                                    >
                                        {isSelected ? (
                                            <CheckSquare size={16} className="text-blue-500 flex-shrink-0" />
                                        ) : (
                                            <Square size={16} className="text-pai-faint flex-shrink-0" />
                                        )}
                                        <FileText size={14} className="text-pai-faint flex-shrink-0" />
                                        <span className={`truncate ${
                                            isSelected
                                                ? 'text-white'
                                                : 'text-pai-meta'
                                        }`}>{doc.name}</span>
                                    </li>
                                );
                            })}
                        </ul>

                        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={handleCancelConfirm}
                                disabled={loading}
                                className="px-4 py-2 text-sm text-pai-secondary bg-white/[0.06] rounded-lg hover:bg-white/[0.09] transition-colors"
                            >
                                {t('commonCancel', 'Cancel')}
                            </button>
                            <button
                                onClick={handleConfirmShare}
                                disabled={loading}
                                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                            >
                                {loading ? t('navSharing', 'Sharing...') : selectedDocIds.size > 0
                                    ? t('navShareWithDocs', 'Share with {n} doc{s}', { n: selectedDocIds.size, s: selectedDocIds.size !== 1 ? 's' : '' })
                                    : t('navShareWithoutDocs', 'Share without docs')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Share form */}
                        <form onSubmit={handleShareClick} className="p-4">
                            <label className="block text-sm font-medium text-pai-secondary mb-2">
                                {t('navShareByEmail', 'Share with user (by email)')}
                            </label>
                            <div className="flex gap-2">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="user@example.com"
                                    className="flex-1 px-3 py-2 text-sm border border-pai rounded-lg bg-white/[0.06] text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                    type="submit"
                                    disabled={loading || loadingDocs || !email.trim()}
                                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {loadingDocs ? (
                                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <UserPlus size={16} />
                                    )}
                                </button>
                            </div>
                            {/* Granular write permission for the new share (T6). Off =
                                the legacy read-only share; On = the recipient's composer
                                is enabled (BE returns is_read_only=false for can_write). */}
                            <div className="flex items-center justify-between mt-3">
                                <div className="flex items-center gap-1.5">
                                    <Pencil size={14} className="text-pai-faint" />
                                    <div>
                                        <span className="text-sm text-pai-secondary">{t('shareCanWrite')}</span>
                                        <span className="block text-xs text-pai-meta">{t('shareCanWriteHint')}</span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    data-testid="share-can-write"
                                    role="switch"
                                    aria-checked={canWrite}
                                    onClick={() => setCanWrite((v) => !v)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex-shrink-0 ${
                                        canWrite ? 'bg-blue-600' : 'bg-white/[0.12] bg-white/[0.07]'
                                    }`}
                                >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        canWrite ? 'translate-x-6' : 'translate-x-1'
                                    }`} />
                                </button>
                            </div>
                            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                            {success && <p className="text-green-500 text-sm mt-2">{success}</p>}
                        </form>

                        {/* Recent contacts */}
                        {availableContacts.length > 0 && (
                            <div className="px-4 pb-3">
                                <h4 className="text-sm font-medium text-pai-secondary mb-2 flex items-center gap-1.5">
                                    <Clock size={14} className="text-pai-faint" />
                                    {t('navRecentContacts', 'Recent Contacts')}
                                </h4>
                                <div className="flex flex-wrap gap-1.5">
                                    {availableContacts.map(contact => (
                                        <button
                                            key={contact.user_id}
                                            onClick={() => handleContactClick(contact.email)}
                                            disabled={loading}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-white/[0.06] text-pai-secondary rounded-full hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-900/30 dark:hover:text-blue-300 disabled:opacity-50 transition-colors"
                                            title={contact.email}
                                        >
                                            <span className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                                {(contact.username || contact.email).charAt(0).toUpperCase()}
                                            </span>
                                            <span className="truncate max-w-[140px]">
                                                {contact.username || contact.email}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Current shares list */}
                        <div className="px-4 pb-4">
                            <h4 className="text-sm font-medium text-pai-secondary mb-2">
                                {t('navSharedWithCount', 'Shared with ({n})', { n: shares.length })}
                            </h4>
                            {shares.length === 0 ? (
                                <p className="text-sm text-pai-meta">{t('navNotSharedWithAnyone', 'Not shared with anyone')}</p>
                            ) : (
                                <ul className="space-y-2">
                                    {shares.map(share => (
                                        <li key={share.id} className="flex items-center justify-between p-2 bg-white/[0.06] rounded-lg">
                                            <div className="min-w-0">
                                                <span className="text-sm font-medium text-white block truncate">
                                                    {share.shared_with_username}
                                                </span>
                                                <span className="text-xs text-pai-meta block truncate">
                                                    {share.shared_with_email}
                                                </span>
                                            </div>
                                            <span
                                                data-testid="share-permission-chip"
                                                data-permission={share.can_write ? 'write' : 'read'}
                                                className={`flex-shrink-0 ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full ${
                                                    share.can_write
                                                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                                                        : 'bg-white/[0.07] bg-white/[0.07] text-pai-secondary'
                                                }`}
                                            >
                                                {share.can_write ? <Pencil size={10} /> : <Eye size={10} />}
                                                {share.can_write ? t('shareCanWrite') : t('shareReadOnly')}
                                            </span>
                                            <button
                                                onClick={() => handleUnshare(share.shared_with_user_id)}
                                                className="flex-shrink-0 ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                                title={t('navRemoveAccess', 'Remove access')}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-pai-subtle">
                            <p className="text-xs text-pai-meta">
                                {t('navShareReadOnlyFooter', 'Shared users get read-only access. They can view messages but cannot send, edit, or delete.')}
                            </p>
                        </div>
                    </>
                )}
            </Modal>
    );
};

export default ShareDialog;
